import { useState } from "react";
import { Sparkles, Play, Image as ImageIcon, X, ChevronLeft, ChevronRight } from "lucide-react";
import { useLanguage } from "@/lib/i18n";

interface ProductImage {
  node: {
    url: string;
    altText: string | null;
  };
}

interface ProductImageGalleryProps {
  images: { edges: ProductImage[] };
  productTitle: string;
}

export function ProductImageGallery({ images, productTitle }: ProductImageGalleryProps) {
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const currentImage = images.edges[selectedImageIndex]?.node;

  return (
    <div>
      {/* Main Image */}
      <div
        className="aspect-square rounded-2xl overflow-hidden bg-white/50 mb-4 shadow-lg cursor-zoom-in"
        onClick={() => currentImage && setLightboxOpen(true)}
      >
        {currentImage ? (
          <img
            src={currentImage.url}
            alt={currentImage.altText || productTitle}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Sparkles className="w-24 h-24 text-primary/30 animate-float" />
          </div>
        )}
      </div>
      
      {/* Thumbnails */}
      {images.edges.length > 1 && (
        <div className="flex gap-2 md:gap-3 overflow-x-auto pb-2 scrollbar-hide">
          {images.edges.map((img, idx) => (
            <button
              key={idx}
              onClick={() => setSelectedImageIndex(idx)}
              className={`w-16 h-16 md:w-20 md:h-20 rounded-lg overflow-hidden flex-shrink-0 border-2 transition-all ${
                idx === selectedImageIndex 
                  ? 'border-primary shadow-lg' 
                  : 'border-transparent hover:border-primary/50'
              }`}
            >
              <img
                src={img.node.url}
                alt={img.node.altText || ''}
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      )}

      {/* Lightbox */}
      {lightboxOpen && currentImage && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center"
          onClick={() => setLightboxOpen(false)}
        >
          <button
            onClick={() => setLightboxOpen(false)}
            className="absolute top-4 end-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
            aria-label="Close"
          >
            <X className="w-6 h-6" />
          </button>

          {images.edges.length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedImageIndex((selectedImageIndex - 1 + images.edges.length) % images.edges.length);
                }}
                className="absolute start-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
                aria-label="Previous image"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedImageIndex((selectedImageIndex + 1) % images.edges.length);
                }}
                className="absolute end-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
                aria-label="Next image"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </>
          )}

          <img
            src={currentImage.url}
            alt={currentImage.altText || productTitle}
            className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}

// Media Gallery Section for GIFs and Videos
interface MediaItem {
  type: 'image' | 'gif' | 'video';
  url?: string;
  placeholder?: boolean;
  caption?: string;
}

interface MediaGallerySectionProps {
  title: string;
  items: MediaItem[];
}

export function MediaGallerySection({ title, items }: MediaGallerySectionProps) {
  const { t } = useLanguage();
  
  return (
    <section className="mb-12 md:mb-16">
      <h2 className="text-2xl md:text-3xl font-bold text-center mb-6 md:mb-10">{title}</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
        {items.map((item, idx) => (
          <div 
            key={idx} 
            className="relative aspect-[4/3] rounded-2xl overflow-hidden bg-white/70 border border-white/80 shadow-sm group"
          >
            {item.url && !item.placeholder ? (
              <>
                {item.type === 'video' ? (
                  <video 
                    src={item.url} 
                    className="w-full h-full object-cover"
                    controls
                    playsInline
                  />
                ) : (
                  <img 
                    src={item.url} 
                    alt={item.caption || ''} 
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                )}
              </>
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-secondary to-pink-100">
                {item.type === 'video' ? (
                  <Play className="w-12 h-12 text-primary/40 mb-2" />
                ) : item.type === 'gif' ? (
                  <div className="relative">
                    <ImageIcon className="w-12 h-12 text-primary/40" />
                    <span className="absolute -bottom-1 -right-1 text-xs font-bold text-primary/60 bg-white/80 px-1 rounded">GIF</span>
                  </div>
                ) : (
                  <ImageIcon className="w-12 h-12 text-primary/40" />
                )}
                <span className="text-sm text-muted-foreground mt-2">
                  {item.type === 'video' ? t('media.videoPlaceholder') : item.type === 'gif' ? t('media.gifPlaceholder') : t('media.imagePlaceholder')}
                </span>
              </div>
            )}
            {item.caption && item.url && (
              <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/60 to-transparent">
                <p className="text-white text-sm">{item.caption}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
